import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  School,
  GraduationCap,
  BookOpen,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  Users,
  Award,
  FileText,
  Building2,
  Download,
  Image as ImageIcon,
  User,
  Clock,
  Video,
  ArrowRight,
  CheckCircle,
  Star,
  Trophy,
  X,
  Facebook,
  Instagram,
  Twitter,
  Share2,
  Copy,
  Check,
  EyeOff
} from 'lucide-react';
import { admissionsService } from '../firebase/admissions';
import { useRealtime } from '../contexts/RealtimeContext';
import toast from 'react-hot-toast';
import ApplicationForm from '../components/admissions/ApplicationForm';
import AdmissionTestCard from '../components/admissions/AdmissionTestCard';
import TestRegistrationModal from '../components/admissions/TestRegistrationModal';
import SEOHead from '../components/SEOHead';
import WhatsAppButton from '../components/common/WhatsAppButton';
import R2Image from '../components/R2Image';

const InstitutionLandingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useRealtime();
  const [institution, setInstitution] = useState(null);
  const [customization, setCustomization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [curriculum, setCurriculum] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [feeStructure, setFeeStructure] = useState(null);
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState('curriculum');
  const [selectedExam, setSelectedExam] = useState('all');
  const [showFeeStructureModal, setShowFeeStructureModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [testConfig, setTestConfig] = useState(null);
  const [isRegisteredForTest, setIsRegisteredForTest] = useState(false);
  const [testRegistration, setTestRegistration] = useState(null);
  const [showTestRegistration, setShowTestRegistration] = useState(false);

  useEffect(() => {
    loadInstitution();
  }, [id]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-menu-container')) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  const loadInstitution = async () => {
    try {
      setLoading(true);
      const [institutionResult, customizationResult, curriculumResult, materialsResult, facultyResult, feeStructureResult, resultsResult, testsResult] = await Promise.all([
        admissionsService.getInstitutionById(id),
        admissionsService.getInstitutionCustomization(id),
        admissionsService.getInstitutionCurriculum(id),
        admissionsService.getInstitutionMaterials(id),
        admissionsService.getInstitutionFaculty(id),
        admissionsService.getInstitutionFeeStructure(id),
        admissionsService.getInstitutionResults(id),
        admissionsService.getAdmissionTests(id)
      ]);

      if (institutionResult.success) {
        const inst = institutionResult.institution;
        
        // Check if institution is hidden (isActive === false)
        // Only allow access if user is admin or institution is active
        if (inst.isActive === false && currentUser?.role !== 'admin') {
          toast.error('This institution page is currently not available');
          navigate('/admissions');
          return;
        }
        
        setInstitution(inst);
      } else {
        toast.error('Institution not found');
        navigate('/admissions');
      }

      if (customizationResult.success && customizationResult.customization) {
        setCustomization(customizationResult.customization);
      }

      if (curriculumResult.success) {
        setCurriculum(curriculumResult.curriculum || []);
      }

      if (materialsResult.success) {
        setMaterials(materialsResult.materials || []);
      }

      if (facultyResult.success) {
        setFaculty(facultyResult.faculty || []);
      }

      if (feeStructureResult.success && feeStructureResult.feeStructure) {
        setFeeStructure(feeStructureResult.feeStructure);
      }

      if (resultsResult.success) {
        setResults(resultsResult.results || []);
      }

      // Load all tests and check registrations
      if (testsResult.success && testsResult.tests && testsResult.tests.length > 0) {
        // For backward compatibility, set the first enabled test as testConfig
        const firstEnabledTest = testsResult.tests.find(t => t.enabled);
        if (firstEnabledTest) {
          setTestConfig(firstEnabledTest);
          
          // Check if user is registered for this test
          if (currentUser) {
            const registrationResult = await admissionsService.isUserRegisteredForTest(firstEnabledTest.id, currentUser.uid);
            if (registrationResult.success && registrationResult.isRegistered) {
              setIsRegisteredForTest(true);
              setTestRegistration(registrationResult.registration);
            }
          }
        }
      }

      // Set initial active tab based on available data
      if (curriculumResult.success && curriculumResult.curriculum?.length > 0) {
        setActiveTab('curriculum');
      } else if (materialsResult.success && materialsResult.materials?.length > 0) {
        setActiveTab('materials');
      } else if (facultyResult.success && facultyResult.faculty?.length > 0) {
        setActiveTab('faculty');
      }
    } catch (error) {
      toast.error('Error loading institution');
      navigate('/admissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTestRegistration = async () => {
    if (!currentUser) {
      toast.error('Please login to register for the test');
      navigate('/login');
      return;
    }

    setShowTestRegistration(true);
  };

  const handleTestRegistrationComplete = async (userData) => {
    try {
      const testId = testConfig?.id;
      if (!testId) {
        toast.error('Test configuration not found');
        return;
      }

      const result = await admissionsService.registerForAdmissionTest(testId, id, currentUser.uid, userData);
      
      if (result.success) {
        toast.success('Successfully registered for admission test!');
        setIsRegisteredForTest(true);
        setShowTestRegistration(false);
        
        // Reload to get registration details
        const registrationResult = await admissionsService.isUserRegisteredForTest(testId, currentUser.uid);
        if (registrationResult.success && registrationResult.isRegistered) {
          setTestRegistration(registrationResult.registration);
        }

        // Show message instead of opening application form
        toast.success('You can now apply for this institute. You can apply now or wait for the results to come out.', {
          duration: 5000,
          icon: '✅'
        });
      } else {
        toast.error('Failed to register: ' + result.error);
      }
    } catch (error) {
      toast.error('Error registering for test: ' + error.message);
    }
  };

  const handleApplyNowClick = () => {
    if (!currentUser) {
      toast.error('Please login to apply');
      navigate('/login');
      return;
    }

    // Check if last date has passed
    const isLastDatePassed = testConfig?.lastDateToRegister 
      ? new Date(testConfig.lastDateToRegister) < new Date()
      : false;

    // Check if registration is closed (manually or by date)
    const isRegistrationClosed = testConfig && (!testConfig.registrationOpen || isLastDatePassed);

    // Check if admission test is mandatory and user hasn't registered
    if (testConfig && testConfig.enabled && testConfig.mandatory && !isRegisteredForTest) {
      if (isRegistrationClosed) {
        toast.error('The admission test registration deadline has passed. Please contact the institution for assistance.');
      } else {
        toast.error('Please register for the admission test first. The test is mandatory for admission.');
        // Scroll to test card
        const testCard = document.querySelector('.admission-test-card');
        if (testCard) {
          testCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the card briefly
          testCard.classList.add('ring-4', 'ring-orange-500', 'ring-opacity-50');
          setTimeout(() => {
            testCard.classList.remove('ring-4', 'ring-orange-500', 'ring-opacity-50');
          }, 2000);
        }
      }
      return;
    }

    // All checks passed, open application form
    setShowApplicationForm(true);
  };

  const getCurrentUrl = () => {
    return window.location.href;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getCurrentUrl());
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => {
        setLinkCopied(false);
        setShowShareMenu(false);
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = async (platform) => {
    const url = getCurrentUrl();
    const title = institution?.name || 'Institution';
    const description = institution?.description || 'Check out this institution';
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedDescription = encodeURIComponent(description);

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: title,
              text: description,
              url: url
            });
            setShowShareMenu(false);
            return;
          } catch (error) {
            // User cancelled or error occurred
            return;
          }
        } else {
          toast.error('Share not supported on this device');
          return;
        }
      default:
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
      setShowShareMenu(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'school':
        return <School className="w-6 h-6" />;
      case 'college':
        return <GraduationCap className="w-6 h-6" />;
      case 'coaching':
        return <BookOpen className="w-6 h-6" />;
      default:
        return <Building2 className="w-6 h-6" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'school':
        return 'bg-blue-100 text-blue-800';
      case 'college':
        return 'bg-purple-100 text-purple-800';
      case 'coaching':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!institution) {
    return null;
  }

  const displayData = customization || institution;

  return (
    <>
      <SEOHead
        title={`${institution.name} - ${institution.type === 'school' ? 'School' : institution.type === 'college' ? 'College' : 'Coaching Center'} | MahStry`}
        description={institution.description || `Learn more about ${institution.name}, a ${institution.type} institution. View courses, faculty, and admission information.`}
        canonical={`https://mahstry.com/institute/${id}`}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Top Navigation Bar with MahStry Branding */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                  <span className="text-white font-bold text-lg">M</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-gray-900">MahStry</span>
                  <span className="text-xs text-gray-500 -mt-1">Landing Page</span>
                </div>
              </Link>
              <div className="flex items-center gap-4">
                <Link
                  to="/admissions"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Browse Institutions
                </Link>
                {currentUser ? (
                  <Link
                    to="/dashboard"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Admin Notice for Hidden Institution */}
        {currentUser?.role === 'admin' && institution.isActive === false && (
          <div className="bg-yellow-500 text-white py-3 px-4">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
              <EyeOff className="w-5 h-5" />
              <span className="font-semibold">Admin View: This institution is currently hidden from public view</span>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white relative overflow-visible">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              {/* Profile Picture/Logo */}
              <div className="w-40 h-40 bg-white rounded-2xl flex items-center justify-center shadow-2xl overflow-hidden">
                {institution.profilePicture ? (
                  <R2Image
                    fileKey={institution.profilePicture}
                    alt={institution.name}
                    className="w-full h-full object-cover"
                  />
                ) : displayData.logo ? (
                  <R2Image
                    fileKey={displayData.logo}
                    alt={institution.name}
                    className="w-full h-full object-contain rounded-2xl"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100">
                    {getTypeIcon(institution.type)}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4 flex-wrap">
                  <h1 className="text-5xl md:text-6xl font-bold">{displayData.name || institution.name}</h1>
                  <span className={`px-4 py-2 rounded-full ${getTypeColor(institution.type)} flex items-center gap-2 text-sm font-semibold`}>
                    {getTypeIcon(institution.type)}
                    <span className="capitalize">{institution.type}</span>
                  </span>
                </div>
                <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl">
                  {displayData.tagline || displayData.description || institution.description || 'Welcome to our institution'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start relative">
                  {currentUser ? (
                    <button
                      onClick={handleApplyNowClick}
                      className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2 text-lg shadow-lg"
                    >
                      <FileText className="w-5 h-5" />
                      Apply Now
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/login')}
                      className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2 text-lg shadow-lg"
                    >
                      <FileText className="w-5 h-5" />
                      Login to Apply
                    </button>
                  )}
                  {institution.virtualTourVideo && (
                    <a
                      href={institution.virtualTourVideo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors flex items-center gap-2 text-lg"
                    >
                      <Video className="w-5 h-5" />
                      Virtual Tour
                    </a>
                  )}
                  <button
                    onClick={() => setShowFeeStructureModal(true)}
                    className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors flex items-center gap-2 text-lg"
                  >
                    <span className="text-lg font-bold">₹</span>
                    Fee Structure
                  </button>
                  <button
                    onClick={() => setShowResultsModal(true)}
                    className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors flex items-center gap-2 text-lg"
                  >
                    <Trophy className="w-5 h-5" />
                    Our Results
                  </button>
                  <div className="relative share-menu-container">
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors flex items-center gap-2 text-lg"
                    >
                      <Share2 className="w-5 h-5" />
                      Share
                    </button>
                    
                    {/* Share Menu Dropdown */}
                    {showShareMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 min-w-[220px] z-[9999] overflow-visible"
                      >
                        {navigator.share && (
                          <button
                            onClick={() => handleShare('native')}
                            className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <Share2 className="w-4 h-4" />
                            Share via...
                          </button>
                        )}
                        <button
                          onClick={handleCopyLink}
                          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          {linkCopied ? (
                            <>
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              Copy Link
                            </>
                          )}
                        </button>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => handleShare('facebook')}
                          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <Facebook className="w-4 h-4 text-blue-600" />
                          Share on Facebook
                        </button>
                        <button
                          onClick={() => handleShare('twitter')}
                          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <Twitter className="w-4 h-4 text-sky-500" />
                          Share on Twitter
                        </button>
                        <button
                          onClick={() => handleShare('whatsapp')}
                          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          Share on WhatsApp
                        </button>
                        <button
                          onClick={() => handleShare('linkedin')}
                          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          Share on LinkedIn
                        </button>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Us</h2>
                <div className="prose max-w-none text-gray-700 leading-relaxed">
                  {displayData.about || displayData.description || institution.description || (
                    <p className="text-gray-600">No description available.</p>
                  )}
                </div>
              </motion.div>

              {/* Admission Test Card */}
              {testConfig && testConfig.enabled && (
                <AdmissionTestCard
                  testConfig={testConfig}
                  onRegister={handleTestRegistration}
                  isRegistered={isRegisteredForTest}
                  registration={testRegistration}
                />
              )}

              {/* Features */}
              {displayData.features && displayData.features.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Features</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {displayData.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                        <Award className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Programs/Courses - Legacy */}
              {displayData.programs && displayData.programs.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Programs Offered</h2>
                  <div className="space-y-3">
                    {displayData.programs.map((program, index) => (
                      <div key={index} className="flex items-center gap-3 text-gray-700 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="font-medium">{program}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Tabs for Curriculum, Materials, and Faculty */}
              {(curriculum.length > 0 || materials.length > 0 || faculty.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl shadow-lg"
                >
                  {/* Tab Buttons */}
                  <div className="border-b border-gray-200 flex flex-wrap gap-2 px-6 pt-6">
                    {curriculum.length > 0 && (
                      <button
                        onClick={() => setActiveTab('curriculum')}
                        className={`px-6 py-3 font-semibold text-sm rounded-t-lg transition-colors ${
                          activeTab === 'curriculum'
                            ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-5 h-5" />
                          <span>Courses & Programs</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            activeTab === 'curriculum' ? 'bg-white/20' : 'bg-gray-200'
                          }`}>
                            {curriculum.length}
                          </span>
                        </div>
                      </button>
                    )}
                    {materials.length > 0 && (
                      <button
                        onClick={() => setActiveTab('materials')}
                        className={`px-6 py-3 font-semibold text-sm rounded-t-lg transition-colors ${
                          activeTab === 'materials'
                            ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <ImageIcon className="w-5 h-5" />
                          <span>Brochures & Posters</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            activeTab === 'materials' ? 'bg-white/20' : 'bg-gray-200'
                          }`}>
                            {materials.length}
                          </span>
                        </div>
                      </button>
                    )}
                    {faculty.length > 0 && (
                      <button
                        onClick={() => setActiveTab('faculty')}
                        className={`px-6 py-3 font-semibold text-sm rounded-t-lg transition-colors ${
                          activeTab === 'faculty'
                            ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          <span>Faculty</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            activeTab === 'faculty' ? 'bg-white/20' : 'bg-gray-200'
                          }`}>
                            {faculty.length}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>

                  {/* Tab Content */}
                  <div className="p-8">
                    {/* Curriculum Tab */}
                    {activeTab === 'curriculum' && curriculum.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Courses & Programs</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {curriculum.map((course) => (
                            <div key={course.id} className="border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow hover:border-blue-300">
                              <h3 className="text-xl font-bold text-gray-900 mb-3">{course.title}</h3>
                              {course.description && (
                                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{course.description}</p>
                              )}
                              <div className="space-y-2 text-sm text-gray-700">
                                {course.duration && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <span>{course.duration}</span>
                                  </div>
                                )}
                                {course.fees && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-400 font-semibold">₹</span>
                                    <span>{course.fees}</span>
                                  </div>
                                )}
                                {course.subjects && (
                                  <div className="flex items-center gap-2">
                                    <BookOpen className="w-4 h-4 text-gray-400" />
                                    <span>{course.subjects}</span>
                                  </div>
                                )}
                                {course.eligibility && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    <span className="font-semibold">Eligibility:</span> {course.eligibility}
                                  </p>
                                )}
                              </div>
                              {course.highlights && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <p className="text-sm text-gray-600 whitespace-pre-line">{course.highlights}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* Materials Tab */}
                    {activeTab === 'materials' && materials.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Brochures & Posters</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {materials.map((material) => (
                            <div key={material.id} className="border-2 border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                              {material.fileUrl && (
                                <div className="h-48 bg-gray-200 flex items-center justify-center">
                                  {material.fileUrl.includes('.pdf') ? (
                                    <FileText className="w-16 h-16 text-red-500" />
                                  ) : (
                                    <R2Image
                                      fileKey={material.fileUrl}
                                      alt={material.title}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              )}
                              <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h3 className="font-bold text-gray-900">{material.title}</h3>
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                                    {material.type === 'brochure' ? 'Brochure' : 'Poster'}
                                  </span>
                                </div>
                                {material.description && (
                                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{material.description}</p>
                                )}
                                {material.fileUrl && (
                                  <a
                                    href={material.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-semibold"
                                  >
                                    <Download className="w-4 h-4" />
                                    View/Download
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* Faculty Tab */}
                    {activeTab === 'faculty' && faculty.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Faculty</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {faculty.map((member) => (
                            <div key={member.id} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                              {member.photoUrl ? (
                                <R2Image
                                  fileKey={member.photoUrl}
                                  alt={member.name}
                                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-lg"
                                />
                              ) : (
                                <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center border-4 border-white shadow-lg">
                                  <User className="w-16 h-16 text-gray-400" />
                                </div>
                              )}
                              <h3 className="font-bold text-gray-900 mb-2 text-lg">{member.name}</h3>
                              {member.designation && (
                                <p className="text-sm text-gray-600 mb-2 font-semibold">{member.designation}</p>
                              )}
                              {member.qualification && (
                                <p className="text-xs text-gray-500 mb-2 flex items-center justify-center gap-1">
                                  <GraduationCap className="w-4 h-4" />
                                  {member.qualification}
                                </p>
                              )}
                              {member.experience && (
                                <p className="text-xs text-gray-500 mb-2">Experience: {member.experience}</p>
                              )}
                              {member.subject && (
                                <p className="text-xs text-gray-600 mb-2 font-medium">Subject: {member.subject}</p>
                              )}
                              {member.bio && (
                                <p className="text-xs text-gray-600 line-clamp-3 mt-2">{member.bio}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-4">
                  {institution.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                      <div className="text-gray-700">
                        <p className="font-medium">{institution.address.street}</p>
                        <p>
                          {institution.address.city}
                          {institution.address.state && `, ${institution.address.state}`}
                        </p>
                        {institution.address.pincode && (
                          <p className="text-sm text-gray-500">PIN: {institution.address.pincode}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {institution.contact?.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <a
                        href={`tel:${institution.contact.phone}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {institution.contact.phone}
                      </a>
                    </div>
                  )}

                  {institution.contact?.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <a
                        href={`mailto:${institution.contact.email}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {institution.contact.email}
                      </a>
                    </div>
                  )}

                  {/* Social Media Links */}
                  {(institution.socialMedia?.facebook || institution.socialMedia?.instagram || institution.socialMedia?.twitter) && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Follow Us</p>
                      <div className="flex items-center gap-3">
                        {institution.socialMedia?.facebook && (
                          <a
                            href={institution.socialMedia.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            title="Facebook"
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                        )}
                        {institution.socialMedia?.instagram && (
                          <a
                            href={institution.socialMedia.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors"
                            title="Instagram"
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                        )}
                        {institution.socialMedia?.twitter && (
                          <a
                            href={institution.socialMedia.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-10 h-10 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                            title="Twitter"
                          >
                            <Twitter className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </motion.div>

              {/* Quick Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Info</h2>
                <div className="space-y-4">
                  {institution.established && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-500">Established</p>
                        <p className="text-gray-900 font-semibold">{institution.established}</p>
                      </div>
                    </div>
                  )}
                  {institution.students && (
                    <div className="flex items-center gap-3">
                      <Users className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-500">Students</p>
                        <p className="text-gray-900 font-semibold">{institution.students}</p>
                      </div>
                    </div>
                  )}
                  {institution.applicationFee && (
                    <div className="flex items-center gap-3">
                      <span className="text-blue-600 text-xl font-bold">₹</span>
                      <div>
                        <p className="text-sm text-gray-500">Application Fee</p>
                        <p className="text-gray-900 font-semibold">₹{institution.applicationFee}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* CTA Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg p-6 text-white"
              >
                <h3 className="text-xl font-bold mb-3">Ready to Apply?</h3>
                <p className="text-blue-100 mb-4 text-sm">
                  Start your application process today and take the first step towards your future.
                </p>
                {currentUser ? (
                  <button
                    onClick={handleApplyNowClick}
                    className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Apply Now
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Login to Apply
                  </button>
                )}
              </motion.div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <Link to="/" className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">M</span>
                  </div>
                  <span className="text-xl font-bold">MahStry</span>
                </Link>
                <p className="text-gray-400 text-sm">
                  This landing page is powered by MahStry Platform. Explore more institutions and educational opportunities.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>
                    <Link to="/admissions" className="hover:text-white transition-colors">
                      Browse Institutions
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="hover:text-white transition-colors">
                      Home
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Contact</h4>
                <p className="text-sm text-gray-400 mb-2">
                  For inquiries about this institution, please use the contact information provided above.
                </p>
                <p className="text-sm text-gray-400">
                  For platform support:{' '}
                  <a
                    href="mailto:support@mahstry.com"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    support@mahstry.com
                  </a>
                  {' '}or{' '}
                  <a
                    href="https://www.instagram.com/mahstry/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Instagram
                  </a>
                </p>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-sm text-gray-400 text-center md:text-left">
                  © {new Date().getFullYear()} Mahstry Private Limited. All rights reserved. This landing page is provided by MahStry Platform.
                </p>
              </div>
            </div>
          </div>
        </footer>

        {/* Application Form Modal */}
        {showApplicationForm && (
          <ApplicationForm
            institution={institution}
            onClose={() => setShowApplicationForm(false)}
            onSuccess={() => {
              setShowApplicationForm(false);
              toast.success('Application submitted successfully!');
            }}
          />
        )}

        {/* Fee Structure Modal */}
        {showFeeStructureModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={() => setShowFeeStructureModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 text-2xl font-bold">₹</span>
                  <h2 className="text-2xl font-bold text-gray-900">Fee Structure</h2>
                </div>
                <button
                  onClick={() => setShowFeeStructureModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                {feeStructure ? (
                  feeStructure.mode === 'pdf' && feeStructure.pdfUrl ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <FileText className="w-5 h-5 text-red-500" />
                        <span>{feeStructure.pdfFileName || 'Fee Structure PDF'}</span>
                      </div>
                      <a
                        href={feeStructure.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                      >
                        <Download className="w-4 h-4" />
                        View/Download Fee Structure
                      </a>
                    </div>
                  ) : feeStructure.entries && feeStructure.entries.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Course/Program</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Fee</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Duration</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feeStructure.entries.map((entry, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3">{entry.course}</td>
                              <td className="border border-gray-300 px-4 py-3">{entry.fee}</td>
                              <td className="border border-gray-300 px-4 py-3">{entry.duration || '-'}</td>
                              <td className="border border-gray-300 px-4 py-3">{entry.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center py-8">No fee structure information available yet.</p>
                  )
                ) : (
                  <p className="text-gray-600 text-center py-8">No fee structure information available yet.</p>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Our Results Modal */}
        {showResultsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={() => setShowResultsModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  <h2 className="text-2xl font-bold text-gray-900">Our Results</h2>
                </div>
                <button
                  onClick={() => setShowResultsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                {/* Exam Filter */}
                {results.length > 0 && [...new Set(results.map(r => r.exam).filter(Boolean))].length > 0 && (
                  <div className="mb-6 flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">Filter by Exam:</span>
                    <button
                      onClick={() => setSelectedExam('all')}
                      className={`px-4 py-2 text-sm rounded-lg border transition-colors font-medium ${
                        selectedExam === 'all'
                          ? 'border-blue-600 bg-blue-50 text-blue-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      All
                    </button>
                    {[...new Set(results.map(r => r.exam).filter(Boolean))].map((exam) => (
                      <button
                        key={exam}
                        onClick={() => setSelectedExam(exam)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-colors font-medium ${
                          selectedExam === exam
                            ? 'border-blue-600 bg-blue-50 text-blue-600'
                            : 'border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {exam}
                      </button>
                    ))}
                  </div>
                )}

                {/* Year-wise Results */}
                {results.length > 0 ? (
                  Object.entries(
                    results
                      .filter(r => selectedExam === 'all' || r.exam === selectedExam)
                      .reduce((acc, result) => {
                        const year = result.year || 'Unknown';
                        if (!acc[year]) {
                          acc[year] = [];
                        }
                        acc[year].push(result);
                        return acc;
                      }, {})
                  )
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([year, yearResults]) => (
                      <div key={year} className="mb-8 last:mb-0">
                        <div className="flex items-center gap-2 mb-4">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <h3 className="text-2xl font-semibold text-gray-900">{year}</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {yearResults.map((result) => (
                            <div
                              key={result.id}
                              className="border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                            >
                              <div className="flex items-center gap-2 mb-4">
                                <Trophy className="w-5 h-5 text-yellow-500" />
                                <h4 className="text-xl font-semibold text-gray-900">{result.exam}</h4>
                              </div>
                              <div className="space-y-2 text-sm">
                                {result.totalStudents && (
                                  <div className="flex items-center gap-2 text-gray-700">
                                    <BookOpen className="w-4 h-4 text-gray-400" />
                                    <span>Total Students: <strong>{result.totalStudents}</strong></span>
                                  </div>
                                )}
                                {result.qualified && (
                                  <div className="flex items-center gap-2 text-gray-700">
                                    <Trophy className="w-4 h-4 text-green-500" />
                                    <span>Qualified: <strong>{result.qualified}</strong></span>
                                  </div>
                                )}
                                {result.topRank && (
                                  <div className="flex items-center gap-2 text-gray-700">
                                    <Trophy className="w-4 h-4 text-yellow-500" />
                                    <span>Top Rank: <strong>{result.topRank}</strong></span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Description - Full display, not truncated */}
                              {result.description && result.description.trim() && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Description</p>
                                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{result.description}</p>
                                </div>
                              )}
                              
                              {/* Highlights - Always show if they exist */}
                              {result.highlights && Array.isArray(result.highlights) && result.highlights.length > 0 && (
                                <div className={`mt-4 pt-4 border-t border-gray-200 ${!result.description || !result.description.trim() ? 'mt-0 pt-0 border-t-0' : ''}`}>
                                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Highlights</p>
                                  <div className="flex flex-wrap gap-2">
                                    {result.highlights.map((highlight, index) => (
                                      highlight && highlight.trim() && (
                                        <span
                                          key={index}
                                          className="text-xs px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-medium"
                                        >
                                          {highlight}
                                        </span>
                                      )
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                ) : (
                  <p className="text-gray-600 text-center py-8">No results information available yet.</p>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Test Registration Modal */}
        {showTestRegistration && (
          <TestRegistrationModal
            onClose={() => setShowTestRegistration(false)}
            onSubmit={handleTestRegistrationComplete}
            currentUser={currentUser}
            testConfig={testConfig}
          />
        )}

        {/* Application Form Modal */}
        {showApplicationForm && (
          <ApplicationForm
            institution={institution}
            onClose={() => setShowApplicationForm(false)}
            onSuccess={() => {
              setShowApplicationForm(false);
              toast.success('Application submitted successfully!');
            }}
            testRegistration={testRegistration}
          />
        )}

        {/* WhatsApp Contact Button */}
        <WhatsAppButton phoneNumber="7395007539" />
      </div>
    </>
  );
};

export default InstitutionLandingPage;

